import { HeatOption } from "@/api/facilities";
import ImpactPill from "@/components/Impact/ImpactPill";
import { useCompanySettings } from "@/hooks/useCompanySettings";
import { useGetFacility } from "@/hooks/useFacilities";
import { useSourceHeatImpact } from "@/hooks/useSourceImpact";
import useTranslation from "@/hooks/useTranslation";
import { unitMapping } from "@/utilities/units";
import { MinusSquareOutlined, PlusSquareOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import {
  Col,
  Form,
  FormListFieldData,
  Input,
  InputNumber,
  Radio,
  Row,
  Segmented,
  Select,
  Space,
  Tooltip,
} from "antd";
import { useWatch } from "antd/es/form/Form";
import _ from "lodash";
import type { FormInstance } from "rc-field-form/es/interface";
import { useState } from "react";
import { multiplyImpactWithFactor } from "../../utils";
import { constructUrn, extractUrn } from "./funcs";
import { HeatSourceLabels } from "./HeatContractModal";
import heatStyles from "./HeatContractModal.module.scss";

const Description = ({ text, optional }: { text: string; optional?: boolean }) => {
  return (
    <Space>
      <span>{text}</span>
      {optional && <span className={heatStyles.formLabelOptional}>(optional)</span>}
    </Space>
  );
};

const AdvancedOptionsToggle = ({
  showAdvanced,
  setShowAdvanced,
}: {
  showAdvanced: boolean;
  setShowAdvanced: any;
}) => {
  return (
    <span
      className={heatStyles.advancedOptionsToggle}
      onClick={() => setShowAdvanced(!showAdvanced)}
    >
      {!showAdvanced ? (
        <>
          <PlusSquareOutlined /> Show advanced options
        </>
      ) : (
        <>
          <MinusSquareOutlined /> Hide advanced options
        </>
      )}
    </span>
  );
};

const ValidateOption = ({
  option,
  scope,
  method,
  type,
  forUnit,
}: {
  option: HeatOption;
  scope?: string;
  method?: string | null;
  type?: string | null;
  forUnit?: boolean;
}) => {
  const urn = extractUrn(option.id);
  const isScopeValid = scope ? urn.scope === scope : true;
  const isMethodValid = method ? urn.method === method : true;
  const isTypeValid = type ? urn.type === type : true;

  // if validating for unit, we don't need to check for method and type labels
  if (forUnit) {
    return isScopeValid && isMethodValid && isTypeValid;
  }

  const validMethodLabel = method === null ? option.method_label !== null : true;
  const validTypeLabel = type === null ? option.type_label !== null : true;

  return isScopeValid && isMethodValid && isTypeValid && validMethodLabel && validTypeLabel;
};

function HeatSource({
  allOptionsData,
  allSources,
  field,
  form,
  facilityId,
  estimation,
  productMass,
}: {
  allOptionsData?: HeatOption[];
  allSources: HeatSourceLabels[];
  field: FormListFieldData;
  form: FormInstance;
  facilityId: number;
  estimation?: boolean;
  productMass?: number;
}) {
  // rerender when sources change to update form options
  useWatch(`sources`, form);

  const [showAdvanced, setShowAdvanced] = useState(false);
  const unit = useWatch(["sources", field.key, "unit"], form);
  const amount = useWatch(["sources", field.key, "amount"], form);
  const percentage = (useWatch(["sources", field.key, "percentage"], form) || 0) / 100;
  const total = Math.round(amount * percentage * 100) / 100;
  const t = useTranslation().utilities;

  const facility = useGetFacility(facilityId);
  const company = useCompanySettings();

  const sourceLabel = allSources?.find(
    (item: any) => form.getFieldValue("sources")[field.key].source === item.source
  )?.source_label;

  const options = allOptionsData?.filter(
    (option) =>
      extractUrn(option.id).source === form.getFieldValue("sources")[field.key].source &&
      extractUrn(option.id).category === form.getFieldValue("sources")[field.key].category
  );

  const centralEnabled = options?.filter((option) => {
    return ValidateOption({
      option,
      scope: "CENTRAL",
      type: form.getFieldValue("sources")[field.key].type,
      method: form.getFieldValue("sources")[field.key].method,
    });
  });

  const districtEnabled = options?.filter((option) => {
    return ValidateOption({
      option,
      scope: "DISTRICT",
      type: form.getFieldValue("sources")[field.key].type,
      method: form.getFieldValue("sources")[field.key].method,
    });
  });

  const methods = _.chain(options)
    .filter(function (option) {
      return ValidateOption({
        option,
        scope: form.getFieldValue("sources")[field.key].scope,
        type: form.getFieldValue("sources")[field.key].type,
        method: null,
      });
    })
    .map(function (option) {
      return {
        value: extractUrn(option.id).method,
        label: option.method_label,
      };
    })
    .uniqBy("value")
    .value();

  const units = _.chain(options)
    .filter(function (option) {
      return ValidateOption({
        option,
        scope: form.getFieldValue("sources")[field.key].scope,
        type: form.getFieldValue("sources")[field.key].type,
        method: form.getFieldValue("sources")[field.key].method,
        forUnit: true,
      });
    })
    .map(function (option) {
      return option.units.map((unit) => {
        return { value: unit, label: unitMapping[unit] || unit };
      });
    })
    .flatten()
    .uniqBy("value")
    .value();

  const scopes = [
    {
      scope: "",
      scopeLabel: "Use average",
      disabled: false,
    },
    {
      scope: "CENTRAL",
      scopeLabel: "Central / small scale",
      disabled: centralEnabled?.length === 0,
    },
    {
      scope: "DISTRICT",
      scopeLabel: "District / industrial",
      disabled: districtEnabled?.length === 0,
    },
  ];

  const types = _.chain(options)
    .filter(function (option) {
      return ValidateOption({
        option,
        scope: form.getFieldValue("sources")[field.key].scope,
        type: null,
        method: form.getFieldValue("sources")[field.key].method,
      });
    })
    .map(function (option) {
      return {
        value: extractUrn(option.id).type,
        label: option.type_label,
      };
    })
    .uniqBy("value")
    .value();

  const { data, isLoading, isFetching } = useSourceHeatImpact({
    facilityId: facilityId,
    sourceUrn: constructUrn({
      category: form.getFieldValue("sources")[field.key].category || "",
      source: form.getFieldValue("sources")[field.key].source || "",
      scope: form.getFieldValue("sources")[field.key].scope || "",
      method: form.getFieldValue("sources")[field.key].method || "",
      type: form.getFieldValue("sources")[field.key].type || "",
    }),

    amount: 1,
    unit: form.getFieldValue("sources")[field.key].unit,
  });

  return (
    <Row>
      <Col
        span={24}
        className={heatStyles.sourceContainer}
      >
        <Form.Item
          key={field.key}
          name={[field.key, "category"]}
          hidden={true}
        >
          <Input />
        </Form.Item>
        <Row>
          <Col
            span={24}
            className={heatStyles.formSourceLabelContainer}
          >
            {sourceLabel}
          </Col>
          <Row
            gutter={[8, 12]}
            align="middle"
          >
            {estimation ? (
              <>
                <Col span={10}>
                  <Space>
                    Consumption estimate per kg
                    <Tooltip title={t.consumptionEstimate}>
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </Space>
                </Col>
                <Col span={10}>
                  <Form.Item
                    noStyle
                    name={[field.key, "amount"]}
                    rules={[
                      {
                        required: true,
                        message: "Consumption is required.",
                      },
                    ]}
                  >
                    <InputNumber
                      className={heatStyles.consumptionFormInput}
                      addonAfter={`${unitMapping[unit]} / kg`}
                    />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item
                    name={[field.key, "unit"]}
                    initialValue={"mj"}
                    noStyle
                  >
                    <Segmented options={units} />
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Space>
                    Total product mass in facility
                    <Tooltip title={t.totalProductMassInFacility}>
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </Space>
                </Col>
                <Col span={10}>
                  <InputNumber
                    className={heatStyles.consumptionFormInput}
                    addonAfter={"kg"}
                    disabled
                    value={productMass}
                  />
                </Col>
                <Col span={4}></Col>
                <Col span={10}>
                  <Space>
                    Total consumption estimate
                    <Tooltip title={t.totalConsumptionEstimate}>
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </Space>
                </Col>
                <Col span={10}>
                  <InputNumber
                    className={heatStyles.consumptionFormInput}
                    addonAfter={unitMapping[unit]}
                    disabled
                    value={productMass ? productMass * amount : 0}
                  />
                </Col>
                <Col span={4}></Col>
              </>
            ) : (
              <>
                <Col span={10}>
                  <Description
                    text={`Total consumed at ${facility.data?.facility_name}`}
                    optional={false}
                  />
                </Col>
                <Col span={10}>
                  <Form.Item
                    noStyle
                    name={[field.key, "amount"]}
                    rules={[
                      {
                        required: true,
                        message: "Consumption is required.",
                      },
                    ]}
                  >
                    <InputNumber
                      className={heatStyles.consumptionFormInput}
                      addonAfter={unitMapping[unit]}
                    />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item
                    noStyle
                    name={[field.key, "unit"]}
                    initialValue={"mj"}
                  >
                    <Segmented options={units} />
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Description
                    text={`Percentage for ${company.data?.company_name}`}
                    optional={false}
                  />
                </Col>
                <Col span={14}>
                  <Form.Item
                    noStyle
                    name={[field.key, "percentage"]}
                    rules={[
                      {
                        required: true,
                        message: "Percentage is required.",
                      },
                    ]}
                  >
                    <InputNumber
                      className={heatStyles.consumptionFormInput}
                      controls={false}
                      min={0}
                      max={100}
                      addonAfter={"%"}
                    />
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Description
                    text={`Total consumed for ${company.data?.company_name}`}
                    optional={false}
                  />
                </Col>
                <Col span={14}>
                  <InputNumber
                    className={heatStyles.consumptionFormInput}
                    controls={false}
                    value={total}
                    addonAfter={unitMapping[unit]}
                    disabled
                  />
                </Col>
              </>
            )}
            <Col span={10}>
              <Description
                text={"Heat production method"}
                optional={true}
              />
            </Col>
            <Col span={14}>
              <Form.Item
                name={[field.key, "method"]}
                noStyle
              >
                <Select
                  className={heatStyles.componentFullWidth}
                  disabled={methods.length === 0}
                  allowClear
                  options={methods}
                />
              </Form.Item>
            </Col>
            <Col span={10}>
              <AdvancedOptionsToggle
                showAdvanced={showAdvanced}
                setShowAdvanced={setShowAdvanced}
              />
            </Col>
            <Col span={14}>
              <ImpactPill
                impact={multiplyImpactWithFactor(
                  data,
                  estimation ? amount * (productMass || 0) : total
                )}
                loading={isLoading || isFetching}
              />
            </Col>
            <Col span={24}>
              <div
                style={{
                  display: showAdvanced ? "block" : "none",
                }}
                className={heatStyles.advancedOptionsContainer}
              >
                <Col span={24}>
                  <Description
                    text={"Size"}
                    optional={true}
                  />
                </Col>
                <Col span={24}>
                  <Form.Item
                    name={[field.key, "scope"]}
                    noStyle
                  >
                    <Radio.Group className={heatStyles.radioGroupScopes}>
                      <Space direction="vertical">
                        {scopes.map((scope) => (
                          <Radio
                            key={scope.scope}
                            value={scope.scope}
                            disabled={scope.disabled}
                          >
                            {scope.scopeLabel}
                          </Radio>
                        ))}
                      </Space>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Description
                    text={"Type"}
                    optional={true}
                  />
                </Col>
                <Col span={24}>
                  <Form.Item
                    name={[field.key, "type"]}
                    style={{ marginBottom: 0 }}
                    noStyle
                  >
                    <Select
                      className={heatStyles.componentFullWidth}
                      disabled={types.length === 0}
                      allowClear
                      options={types}
                    />
                  </Form.Item>
                </Col>
              </div>
            </Col>
          </Row>
        </Row>
      </Col>
    </Row>
  );
}

export default HeatSource;
